/**
 * ============================================================================
 * EnrollNowSticky — Controller (client)
 * ============================================================================
 * Owns brochure / counsellor / call / WhatsApp CTA handlers. useCallback
 * + props refs keep handlers stable across parent re-renders.
 * Behavior preserved from pre-MVC EnrollNowSticky.
 * **************************************************************************
 */
"use client";

import { useCallback, useRef } from "react";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import { usePathname } from "next/navigation";
import type {
  EnrollNowStickyProps,
  EnrollNowStickyViewModel,
} from "../model/enrollNowSticky.types";

export function useEnrollNowStickyController(
  props: EnrollNowStickyProps,
): EnrollNowStickyViewModel | null {
  const {
    associatedPrograms = [],
    isHomePage = false,
    isHiddenInitial = false,
    forceHomeVariant = false,
    showCounsellingCta = false,
  } = props;

  const modalDispatch = useModalDispatch();
  const pathName = usePathname();

  const associatedProgramsRef = useRef(associatedPrograms);
  associatedProgramsRef.current = associatedPrograms;

  const isRealHomePageUrl = pathName == "/";
  const isHomePageUrl = forceHomeVariant || isRealHomePageUrl;
  const isAONSATPage = pathName == "/aonsat";
  const isHidden = isHiddenInitial || isRealHomePageUrl;
  const useCounsellingCta = showCounsellingCta || isHomePageUrl;

  const handleWhatsAppBtnClicked = useCallback(() => {
    pushToDataLayer({
      event: "call_chat_interaction",
      cta_text: "whatsapp_chat",
      section_name: "sticky_sidetray",
      page_type: getGlobalStateValue("pageType"),
      page_url: window.location.href,
    });

    if (window !== undefined) {
      window.open(
        "https://api.whatsapp.com/send/?phone=919818795446&text=Hi&type=phone_number&app_absent=0&utm_source=WAEngChatBot",
        "_target",
      );
    }
  }, []);

  const downloadBrochureScneario = useCallback(() => {
    const programs = associatedProgramsRef.current;
    const brochureLink =
      programs[0]?.attributes?.BasicDetails?.Brochure?.data?.attributes?.url;

    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMba,
        modalData: {
          modalVariant: "downloadBrochure",
          attachmentURL: brochureLink,
          associatedPrograms: programs,
          rfiCase: "DOWNLOAD_BROCHURE_CASE",
        },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Download Brochure",
        section_name: "footer_sticky",
        header_title: "NA",
        page_type: "Home Page",
        page_url: window.location.href || "NA",
      });

      updateStateSession({
        parentSection: "footer_sticky",
        leadStartingCta: "Download Brochure",
      });
    }, 2000);
  }, [modalDispatch]);

  const handleCallBtnClicked = useCallback(() => {
    pushToDataLayer({
      event: "call_chat_interaction",
      cta_text: "call_us",
      section_name: "footer_sticky",
      page_type: getGlobalStateValue("pageType"),
      page_url: window.location.href,
    });

    if (window !== undefined) {
      window.open("tel:18001023434", "_self");
    }
  }, []);

  const handleTalkToCounsellor = useCallback(() => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalV2,
        modalData: {
          heading: "Take the 1st step towards your success story",
          submitBtnText: "Book a Free Session",
          isLandingPage: true,
          associatedPrograms: associatedProgramsRef.current,
        },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Talk to a Counsellor",
        section_name: "footer_sticky",
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      updateStateSession({
        parentSection: "footer_sticky",
        leadStartingCta: "Talk to a Counsellor",
      });
    }, 1500);
  }, [modalDispatch]);

  if (isAONSATPage) {
    return null;
  }

  return {
    isHomePage,
    isHomePageUrl,
    isHidden,
    useCounsellingCta,
    handleWhatsAppBtnClicked,
    downloadBrochureScneario,
    handleCallBtnClicked,
    handleTalkToCounsellor,
  };
}
